import { notify } from '@billy/design-system'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import {
  CreateEInvoiceKYCDocumentParams,
  useCreateEInvoiceKYCDocument,
} from '../../../hooks/useCreateEInvoiceKYCDocument'
import { useEInvoicingSetup } from '../../../hooks/useEInvoicingSetup'
import { useUploadEInvoiceFile } from '../../../hooks/useUploadEInvoiceFile'
import { DocumentType } from '../../../types'
import { MIME_TYPE_TO_FILE_TYPE_MAP } from '../constants/mimeTypeToFileTypeMap'
import { isValidMimeType } from '../utils'

export const useEInvoicingGenericUpload = (documentType: DocumentType) => {
  const { t } = useTranslation()

  const { organization } = useUserOrganization()
  const { goToNextStep, kycDocuments } = useEInvoicingSetup()

  const [file, setFile] = useState<File | undefined>(undefined)
  const document = useMemo(() => kycDocuments?.find((doc) => doc.type === documentType), [kycDocuments, documentType])
  const previewUrlFromFile = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file])
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(document?.signedUrl || previewUrlFromFile)
  const [createDocumentParams, setCreateDocumentParams] = useState<CreateEInvoiceKYCDocumentParams | undefined>(
    undefined,
  )
  const fileType = useMemo(() => {
    const mimeType = file?.type ?? document?.mimeType

    return mimeType && isValidMimeType(mimeType) ? MIME_TYPE_TO_FILE_TYPE_MAP[mimeType] : undefined
  }, [document, file])

  const { uploadKycFile, isProcessing: isFileBeingUploaded } = useUploadEInvoiceFile({
    organizationId: organization?.id,
    onError: () => {
      notify({
        id: NotificationKeys.EInvoiceKYCUploadFileError,
        message: t('e_invoice.kyc.step.upload.notify.upload_file.error'),
        variant: 'error',
      })
    },
  })

  const { createKYCDocument, isProcessing: isDocumentBeingCreated } = useCreateEInvoiceKYCDocument({
    documentType,
    onError: () => {
      notify({
        id: NotificationKeys.EInvoiceKYCCreateDocumentError,
        message: t('e_invoice.kyc.step.upload.notify.create_document.error'),
        variant: 'error',
      })
    },
  })

  const onDropAccepted = useCallback(
    async (files: File[]) => {
      const [file] = files

      const uploadParams = await uploadKycFile(file)
      if (!uploadParams) {
        return
      }
      setFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      setCreateDocumentParams({ fileRef: uploadParams.fileKey, mimeType: file.type, signature: uploadParams.signature })
    },
    [uploadKycFile],
  )

  const handleContinueToNextStep = useCallback(async () => {
    if (createDocumentParams) {
      await createKYCDocument(createDocumentParams)
      queryClient.invalidateQueries([QueryKeys.EInvoicingGetKYCStatus, 'PDP', organization?.id])
      setFile(undefined)
      goToNextStep()
    }
  }, [createKYCDocument, goToNextStep, organization?.id, createDocumentParams])

  const handleDeleteButtonClick = useCallback(() => {
    setCreateDocumentParams(undefined)
    setFile(undefined)
    setPreviewUrl(undefined)
  }, [])

  useEffect(() => {
    if (document) {
      setCreateDocumentParams({
        fileRef: document.fileKey,
        signature: document.signature,
        mimeType: document.mimeType,
      })
    }
  }, [document])

  return {
    fileType,
    handleContinueToNextStep,
    handleDeleteButtonClick,
    onDropAccepted,
    isDocumentBeingCreated,
    isFileBeingUploaded,
    previewUrl,
  }
}
